/**
 * COMPATIBILITY SHIM - Phase 2 Refactoring
 *
 * This is a thin wrapper around the 8 new context slices.
 * It merges all slice values and exports them via useCartContext() for backward compatibility.
 *
 * ⚠️ DEPRECATED: New code should import individual hooks:
 *   - useAppointment() from Context/Appointment
 *   - useLocation() from Context/Location
 *   - useGuests() from Context/Guests
 *   - useSchedule() from Context/Schedule
 *   - useCustomer() from Context/Customer
 *   - useBookingSession() from Context/BookingSession
 *   - useBookingTimer() from Context/Timer
 *   - useUI() from Context/UI
 *
 * This shim will be REMOVED in Phase 3 (after all consumers are migrated).
 * Estimated removal date: Q3 2026 (after consumer migration completes).
 *
 * MIGRATION STATUS:
 * ├─ Phase 1: Analysis ✅
 * ├─ Phase 2A: Slices created ✅
 * ├─ Phase 2B: Compat shim (this file) ✅
 * ├─ Phase 2C: Consumer migration 🔄 (in progress)
 * └─ Phase 3: Cleanup & finalization 📋 (planned)
 */

import { createContext, useMemo } from 'react';
import { AppointmentProvider, useAppointment } from '../Appointment/AppointmentContext';
import { LocationProvider, useLocation } from '../Location/LocationContext';
import { GuestsProvider, useGuests } from '../Guests/GuestsContext';
import { ScheduleProvider, useSchedule } from '../Schedule/ScheduleContext';
import { CustomerProvider, useCustomer } from '../Customer/CustomerContext';
import { BookingSessionProvider, useBookingSession } from '../BookingSession/BookingSessionContext';
import { BookingTimerProvider, useBookingTimer } from '../Timer/useBookingTimer';
import { UIProvider, useUI } from '../UI/UIContext';

export const CartContext = createContext();

/**
 * Provider wrapper that composes all slice providers in nested structure.
 * This exists only for backward compatibility; new code should use individual providers.
 */
export const CartProvider = ({ children }) => {
  return (
    <AppointmentProvider>
      <CartProviderInner>
        {children}
      </CartProviderInner>
    </AppointmentProvider>
  );
};

/**
 * Inner provider that reads appointmentTypeID from AppointmentContext
 * to derive isSingleBooking and isGroupBooking for GuestsProvider.
 */
const CartProviderInner = ({ children }) => {
  const { isSingleBooking, isGroupBooking } = useAppointment();

  return (
    <LocationProvider>
      <GuestsProvider isSingleBooking={isSingleBooking} isGroupBooking={isGroupBooking}>
        <ScheduleProvider>
          <CustomerProvider>
            <BookingSessionProvider>
              <BookingTimerProvider>
                <UIProvider>
                  {children}
                </UIProvider>
              </BookingTimerProvider>
            </BookingSessionProvider>
          </CustomerProvider>
        </ScheduleProvider>
      </GuestsProvider>
    </LocationProvider>
  );
};

/**
 * Backward-compatible hook that merges all slice values.
 * This exists only for old code during migration.
 *
 * ⚠️ DEPRECATED: Use individual hooks instead
 *
 * Old way:
 *   const { appointmentTypeID, members, selectedDate, cartTotal } = useCartContext();
 *
 * New way:
 *   const { appointmentTypeID } = useAppointment();
 *   const { members } = useGuests();
 *   const { selectedDate } = useSchedule();
 *   const { selectCartTotal } = useGuests();
 */
export const useCartContext = () => {
  const appointment = useAppointment();
  const location = useLocation();
  const guests = useGuests();
  const schedule = useSchedule();
  const customer = useCustomer();
  const bookingSession = useBookingSession();
  const timer = useBookingTimer();
  const ui = useUI();

  return useMemo(
    () => ({
      // ===== APPOINTMENT SLICE =====
      appointmentTypeID: appointment.appointmentTypeID,
      setAppointmentTypeID: appointment.setAppointmentTypeID,
      isSingleBooking: appointment.isSingleBooking,
      isGroupBooking: appointment.isGroupBooking,
      isMemberFlowBooking: appointment.isMemberFlowBooking,

      // ===== LOCATION SLICE =====
      selectedOutletID: location.selectedOutletID,
      setSelectedOutletID: location.setSelectedOutletID,
      selectedOutletContact: location.selectedOutletContact,
      setSelectedOutletContact: location.setSelectedOutletContact,
      selectedOutletWhatsapp: location.selectedOutletWhatsapp,
      setSelectedOutletWhatsapp: location.setSelectedOutletWhatsapp,
      outletTypeID: location.outletTypeID,
      setOutletTypeID: location.setOutletTypeID,

      // ===== GUESTS SLICE =====
      members: guests.members,
      activeGuestId: guests.activeGuestId,
      setActiveGuestId: guests.setActiveGuestId,
      selectedMember: guests.selectedMemberIndex,
      setSelectedMember: (index) => {
        const memberId = guests.members[index]?.id;
        if (memberId) guests.setActiveGuestId(memberId);
      },
      updateMemberList: guests.updateMemberServices,
      addGuest: guests.addGuest,
      removeGuest: guests.removeGuest,
      resetGroupBookingMembers: guests.resetMembers,

      // Derived guest values
      selectedServices: guests.selectedServices,
      serviceCount: guests.serviceCount,
      selectedServicesID: guests.selectedServicesID,
      cartTotal: guests.selectCartTotal(location.selectedOutletID ? true : false), // Dummy membership
      activeGuestServiceCount: guests.activeGuestServiceCount,
      activeGuestSubtotal: guests.selectActiveGuestSubtotal(true), // Dummy membership
      groupNextEnabled: guests.selectGroupNextEnabled(),
      memberCount: guests.memberCount,

      // ===== SCHEDULE SLICE =====
      selectedDate: schedule.selectedDate,
      setSelectedDate: schedule.setSelectedDate,
      selectedTime: schedule.selectedTime,
      setSelectedTime: schedule.setSelectedTime,

      // ===== CUSTOMER SLICE =====
      formData: customer.formData,
      setFormData: customer.setFormData,
      isUserVerified: customer.isUserVerified,
      setIsUserVerified: customer.setIsUserVerified,
      verifiedMobile: customer.verifiedMobile,
      setVerifiedMobile: customer.setVerifiedMobile,
      userID: customer.userID,
      setUserID: customer.setUserID,

      // ===== BOOKING SESSION SLICE =====
      bookingID: bookingSession.bookingID,
      setBookingID: bookingSession.setBookingID,
      uuid: bookingSession.uuid,
      setUuid: bookingSession.setUuid,
      occupiedTime: bookingSession.occupiedTime,
      setOccupiedTime: bookingSession.setOccupiedTime,

      // ===== TIMER SLICE =====
      timeLeft: timer.timeLeft,
      setTimeLeft: timer.setTimeLeft,
      isTimerActive: timer.isTimerActive,
      setIsTimerActive: timer.setIsTimerActive,
      isTimesup: timer.isTimesup,
      setIsTimesup: timer.setIsTimesup,
      // isTimerExtend: timer.isTimerExtend,
      // setIsTimerExtend: timer.setIsTimerExtend,
      startTimer: timer.startTimer,
      extendTime: timer.extendTime,
      resetTimer: timer.resetTimer,

      // ===== UI SLICE =====
      isExitCardActive: ui.isExitCardActive,
      setIsExitCardActive: ui.setIsExitCardActive,
      ExitCardShow: () => ui.setIsExitCardActive(true),
      ExitCardHide: () => ui.setIsExitCardActive(false),
      isCartActive: ui.isCartActive,
      setIsCartActive: ui.setIsCartActive,
      showServicesPopup: ui.showServicesPopup,
      setShowServicesPopup: ui.setShowServicesPopup,
      error: ui.error,
      setError: ui.setError,
      isLoadingMainActive: ui.isLoadingMainActive,
      setIsLoadingMainActive: ui.setIsLoadingMainActive,

      // Stubs for values that were moved to local state or deleted
      // (included so old imports don't break immediately)
      isTimeSlotIsDisabled: false,
      setIsTimeSlotIsDisabled: () => {},
      reloadTimeSlots: false,
      setReloadTimeSlots: () => {},
      serviceForTherapist: [],
      setServiceForTherapist: () => {},
      memberForTherapist: [],
      setMemberForTherapist: () => {},
      locationSelectFirst: false,
      setLocationSelectFirst: () => {},
      timeSelectFirst: false,
      setTimeSelectFirst: () => {},
      isCounterDisable: false,
      setIsCounterDisable: () => {},
      count: 0,
      setCount: () => {},
      isSelfAppointment: false,
      setIsSelfAppointment: () => {},
      isGroupAppointment: false,
      setIsGroupAppointment: () => {},
      isOutletSelected: false,
      setIsOutletSelected: () => {},
      selectedOutletTherapist: [],
      setSelectedOutletTherapist: () => {},
      therapistData: [],
      setTherapistData: () => {},
      serviceList: [],
      setServiceList: () => {},
      therapistAtTime: [],
      setTherapistAtTime: () => {},
      grandTotal: 0,
      setGrandTotal: () => {},
      gstAmount: 0,
      setGstAmount: () => {},
      isPopupTherapistAvailbility: false,
      setIsPopupTherapistAvailbility: () => {},
      perMemberSirviceCount: null,
      setPerMemberSirviceCount: () => {},
      depositMoney: 20,
      setDepositMoney: () => {},
      tempBookingID: null,
      setTempBookingID: () => {},
      isAnyServiceWithoutTherapist: true,
      setIsAnyServiceWithoutTherapist: () => {},
      outletType: null,
      setOutletType: () => {},
      addToCart: () => {},
      removeFromCart: () => {},
    }),
    [appointment, location, guests, schedule, customer, bookingSession, timer, ui]
  );
};

export default CartContext;
